/**
 * Weather FX (P1-T12, SPEC-10 §3): camera-following rain and snow particles.
 * Wet surface response lives in WeatherPlugin (materials/plugins.ts); puddle
 * masks for roads live there too (KN_PUDDLE define, road material only).
 *
 * Recipe from SPEC-10 §3: emitter is a box 4 m above and 12 m ahead of the
 * camera; Low 1,400 / Mid 3,200 / High 6,500 particles; stretched billboards;
 * wind adds slant. Snow = 60 % of the rain count, standard billboards.
 * LDR blending (BLENDMODE_STANDARD) on all tiers here (SPEC-10 §3 last
 * paragraph: HDR-pipeline particle check is a P7 on-GPU task).
 */
import { Color4, DynamicTexture, ParticleSystem, Texture, Vector3, type Scene } from 'babylonjs';

export type RainTierCounts = 1400 | 3200 | 6500;

function streakTexture(scene: Scene): Texture {
  const size = 32;
  const tex = new DynamicTexture('kn-rain-tex', { width: size, height: size }, scene, false);
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, size, size);
  // soft vertical streak: bright core, feathered sides, faded ends
  const grad = ctx.createLinearGradient(0, 0, size, 0);
  grad.addColorStop(0.0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.42, 'rgba(255,255,255,0.55)');
  grad.addColorStop(0.5, 'rgba(255,255,255,1)');
  grad.addColorStop(0.58, 'rgba(255,255,255,0.55)');
  grad.addColorStop(1.0, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  // fade the vertical ends so the streak has soft tips
  const fade = ctx.createLinearGradient(0, 0, 0, size);
  fade.addColorStop(0, 'rgba(0,0,0,1)');
  fade.addColorStop(0.18, 'rgba(0,0,0,0)');
  fade.addColorStop(0.82, 'rgba(0,0,0,0)');
  fade.addColorStop(1, 'rgba(0,0,0,1)');
  const gco = ctx as unknown as { globalCompositeOperation: string };
  gco.globalCompositeOperation = 'destination-out';
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, size, size);
  gco.globalCompositeOperation = 'source-over';
  tex.update();
  tex.hasAlpha = true;
  return tex;
}

function flakeTexture(scene: Scene): Texture {
  const size = 16;
  const tex = new DynamicTexture('kn-snow-tex', { width: size, height: size }, scene, false);
  const ctx = tex.getContext();
  ctx.clearRect(0, 0, size, size);
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.55, 'rgba(255,255,255,0.7)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  tex.update();
  tex.hasAlpha = true;
  return tex;
}

/** Rain + snow particle FX; call update(camPos, forward, weather, dt) per frame. */
export class WeatherFx {
  private readonly rain: ParticleSystem;
  private readonly snow: ParticleSystem;
  private readonly rainCap: number;
  private readonly emitterR = new Vector3();
  private readonly emitterF = new Vector3();
  private lastRate = -1;
  private lastSnowRate = -1;
  private lastSlant = -999;
  private enabled: boolean;

  constructor(scene: Scene, cap: RainTierCounts, enabled: boolean) {
    this.enabled = enabled;
    this.rainCap = cap;

    this.rain = new ParticleSystem('kn-rain', cap, scene);
    this.rain.particleTexture = streakTexture(scene);
    this.rain.emitter = new Vector3(0, 4, 0);
    this.rain.minEmitBox = new Vector3(-16, 0, -16);
    this.rain.maxEmitBox = new Vector3(16, 2.5, 16);
    this.rain.direction1 = new Vector3(-1.5, -13, -1.5);
    this.rain.direction2 = new Vector3(1.5, -17, 1.5);
    this.rain.minEmitPower = 0.85;
    this.rain.maxEmitPower = 1.15;
    this.rain.gravity = new Vector3(0, -6, 0);
    this.rain.minLifeTime = 0.55;
    this.rain.maxLifeTime = 0.9;
    this.rain.minSize = 0.035;
    this.rain.maxSize = 0.085;
    this.rain.emitRate = 0;
    this.rain.color1 = new Color4(0.75, 0.82, 0.92, 0.42);
    this.rain.color2 = new Color4(0.62, 0.72, 0.85, 0.3);
    this.rain.colorDead = new Color4(0.6, 0.7, 0.85, 0);
    this.rain.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    // streak the billboard along the fall direction (prototype recipe)
    this.rain.billboardMode = ParticleSystem.BILLBOARDMODE_STRETCHED;
    this.rain.start();

    this.snow = new ParticleSystem('kn-snow', Math.round(cap * 0.6), scene);
    this.snow.particleTexture = flakeTexture(scene);
    this.snow.emitter = new Vector3(0, 4, 0);
    this.snow.minEmitBox = new Vector3(-18, 0, -18);
    this.snow.maxEmitBox = new Vector3(18, 3, 18);
    this.snow.direction1 = new Vector3(-0.5, -1.1, -0.5);
    this.snow.direction2 = new Vector3(0.5, -1.8, 0.5);
    this.snow.minEmitPower = 0.6;
    this.snow.maxEmitPower = 1.0;
    this.snow.minLifeTime = 4.5;
    this.snow.maxLifeTime = 8;
    this.snow.minSize = 0.05;
    this.snow.maxSize = 0.16;
    this.snow.emitRate = 0;
    this.snow.color1 = new Color4(1, 1, 1, 0.85);
    this.snow.color2 = new Color4(0.9, 0.94, 1, 0.7);
    this.snow.colorDead = new Color4(0.9, 0.94, 1, 0);
    this.snow.blendMode = ParticleSystem.BLENDMODE_STANDARD;
    this.snow.gravity = new Vector3(0, -0.35, 0);
    this.snow.start();
  }

  /**
   * Per-frame: keep the emitter box above+ahead of the camera (SPEC-10 §3:
   * 4 m up, 12 m ahead), scale emit rates by weather, slant by wind.
   */
  update(
    camPos: Vector3,
    forward: Vector3,
    weather: { rain: number; snow: number; wind: number },
    dtS: number,
  ): void {
    if (!this.enabled) return;
    // smooth ground-projected forward, normalised
    this.emitterF.set(forward.x, 0, forward.z);
    const len = this.emitterF.length();
    if (len > 1e-4) this.emitterF.scaleInPlace(1 / len);
    this.emitterR.copyFrom(camPos).addInPlace(this.emitterF.scale(12));
    this.emitterR.y = camPos.y + 4;
    (this.rain.emitter as Vector3).copyFrom(this.emitterR);
    (this.snow.emitter as Vector3).copyFrom(this.emitterR);

    // particles fall with the world while the emitter rides the camera;
    // give a horizontal drift with wind (rain slant)
    const slant = weather.wind * 2.2;
    if (Math.abs(slant - this.lastSlant) > 0.05) {
      this.rain.direction1.x = -slant * 0.7 - 0.6;
      this.rain.direction2.x = -slant * 1.3 + 0.6;
      this.rain.direction1.z = -0.6;
      this.rain.direction2.z = 0.6;
      this.snow.direction1.x = -slant * 0.5 - 0.25;
      this.snow.direction2.x = -slant * 0.9 + 0.25;
      this.lastSlant = slant;
    }

    // rate = capacity / mean life, scaled by intensity (quantised to avoid
    // churn: only touch the systems when the rate moved > 5 %)
    const target = Math.round(this.rainCap * weather.rain);
    if (Math.abs(target - this.lastRate) > Math.max(20, this.lastRate * 0.05)) {
      this.rain.emitRate = target;
      this.lastRate = target;
    }
    const snowCap = Math.round(this.rainCap * 0.6);
    const snowTarget = Math.round(snowCap * weather.snow * 0.14); // life ~7 s
    if (Math.abs(snowTarget - this.lastSnowRate) > Math.max(5, this.lastSnowRate * 0.05)) {
      this.snow.emitRate = snowTarget;
      this.lastSnowRate = snowTarget;
    }
    void dtS;
  }

  /** Pin particle time (Babylon advances particles on engine dt, which our
   *  sim freeze does NOT stop): updateSpeed=0 freezes position + life exactly. */
  setFrozen(v: boolean): void {
    this.rain.updateSpeed = v ? 0 : 1;
    this.snow.updateSpeed = v ? 0 : 1;
  }

  stats(): { rain: number; snow: number; rainRate: number } {
    return {
      rain: this.rain.getActiveCount(),
      snow: this.snow.getActiveCount(),
      rainRate: this.rain.emitRate,
    };
  }

  dispose(): void {
    this.rain.dispose();
    this.snow.dispose();
  }
}
